import { useCallback, useEffect, useRef } from 'react';

import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { Dialog, DialogHandle } from 'components/modal_manager';
import { useMyAccount } from 'pages/account/my_account/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { passMeetsRequirements } from 'utils';
import { useSuperState } from 'hooks';
import { UsersApi } from 'apis';
import { useApp } from 'app';

type Field = 'newPassword' | 'oldPassword' | 'confirmPassword';

export const Password = () => {

    const [state, setState, resetState] = useSuperState({
        confirmPassword: '',
        loading: false,
        newPassword: '',
        oldPassword: '',
        oldPasswordValid: true,
        passwordsMatch: true,
        requirementsMet: true,
        visibility: {
            confirmPassword: false,
            newPassword: false,
            oldPassword: false
        }
    });
    const { confirmPassword, loading, newPassword, oldPassword, oldPasswordValid, passwordsMatch, requirementsMet, visibility } = state;

    const { permissions } = useMyAccount();
    const { currentUser, refreshAppBasics } = useApp();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    // A brand-new admin created with a temporary password still carries the
    // NeedsPasswordReset claim. They are redirected here on login and must be able
    // to change their password even without the account-page edit permissions.
    const needsPasswordReset = !!currentUser?.needsPasswordReset;
    const canEdit = needsPasswordReset || (!!permissions?.Edit && !!permissions?.UserEdit);

    const confirmationDialogRef = useRef<DialogHandle>(null);

    const handleChange = e => {

        const { target } = e;
        setState({ [target.id]: target.value });

    };

    const toggleHide = (target: Field) => {

        setState({
            visibility: {
                ...state.visibility,
                [target]: !state.visibility[target]
            }
        });

    };

    const comparePasswords = () => setState({ passwordsMatch: newPassword.trim() === confirmPassword.trim() });

    const checkRequirements = () => setState({ requirementsMet: passMeetsRequirements(newPassword) });

    const attemptPasswordChange = async () => {

        if (!canEdit) return;

        const requirementsMet = passMeetsRequirements(newPassword);
        const passwordsMatch = newPassword.trim() === confirmPassword.trim();
        setState({
            oldPasswordValid: true,
            passwordsMatch,
            requirementsMet
        });

        if (!requirementsMet || !passwordsMatch) return;

        try {

            const success = await UsersApi.ResetCurrentAdminPassword(oldPassword, newPassword);
            if (!success) return setState({ oldPasswordValid: false });

            createToast(formatMessage({ id: 'password_updated_successfully.251aa3ec', defaultMessage: 'Password updated successfully.' }), 'success');
            resetState();

            // Clearing the temporary password clears the claim server-side; refresh
            // app basics so needsPasswordReset flips off and the login redirect stops.
            if (needsPasswordReset) await refreshAppBasics();

        } catch (e){

            console.error(e);
            createToast(formatMessage({ id: 'failed_to_update_password.090443f3', defaultMessage: 'Failed to update password.' }), 'danger');

        }

    };

    const handleSubmit = e => {

        e.preventDefault();
        e.stopPropagation();

        const dialog = confirmationDialogRef?.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction(),
                dialog.OKAction({
                    label: formatMessage({ id: 'reset.43ed980c', defaultMessage: 'Reset' }),
                    onClick: attemptPasswordChange
                })
            ],
            body: (
                <p>
                    <FormattedMessage defaultMessage="Are you sure you want to reset your password?" id="are_you_sure_you_want_to_reset_your_pass.430bf00f" />
                </p>
            ),
            title: formatMessage({ id: 'confirm_password_reset.3c9df366', defaultMessage: 'Confirm password reset' })
        });

    };

    const getFieldType = useCallback((field: Field) => state.visibility[field] ? 'text' : 'password', [state.visibility]);

    useEffect(() => () => resetState(), []);

    return (
        <div className={'password'}>
            <div className="d-flex justify-content-between mb-3">
                <h3 className="text-strong">
                    <FormattedMessage defaultMessage="Reset Password" id="reset_password.9c0200aa" />
                </h3>
            </div>

            {needsPasswordReset && (
                <Alert className="mb-3" variant="info">
                    <b>
                        <FormattedMessage defaultMessage="Set your password to continue" id="set_your_password_to_continue.5e6713be" />
                    </b>
                    <div>
                        <FormattedMessage defaultMessage="Your account was created with a temporary password. For your security, please set a new password. Enter your temporary password as the old password below." id="your_account_was_created_with_a_temporar.05dd266e" />
                    </div>
                </Alert>
            )}

            <Row>
                <Col as={Form} className={'d-flex flex-column gap-3'} onSubmit={handleSubmit} sm={4} xs={12} >

                    {/* For users with multiple accounts stored in a password manager, this hints at which account should be updated */}
                    <input
                        readOnly
                        aria-hidden={true}
                        autoComplete={'username'}
                        className={'d-none'}
                        name={'username'}
                        tabIndex={-1}
                        type={'text'}
                        value={currentUser?.userName ?? currentUser?.email ?? ''}
                    />
                    <Form.Group>
                        <Form.Label htmlFor={'oldPassword'} >
                            <FormattedMessage defaultMessage="Old password:" id="old_password.c16142b4" />
                        </Form.Label>

                        <InputGroup hasValidation>
                            <Form.Control
                                required
                                autoComplete='new-password'
                                id={'oldPassword'}
                                isInvalid={!oldPasswordValid}
                                onChange={handleChange}
                                type={getFieldType('oldPassword')}
                                value={oldPassword}
                            />
                            <Button aria-label={visibility.oldPassword ? (formatMessage({ id: 'hide_password.b11980db', defaultMessage: 'Hide password' })) : (formatMessage({ id: 'show_password.774881b8', defaultMessage: 'Show password' }))} aria-pressed={visibility.oldPassword} onClick={() => toggleHide('oldPassword')} size={'sm'} variant={visibility.oldPassword ? 'primary' : 'secondary'}>
                                <i aria-hidden="true" className={`fa-solid ${!visibility.oldPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                            </Button>
                            <Form.Control.Feedback type="invalid">
                                {'Password is incorrect.'}
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor={'newPassword'}>
                            <FormattedMessage defaultMessage="New password:" id="new_password.a39bf482" />
                        </Form.Label>
                        <InputGroup hasValidation>
                            <Form.Control
                                required
                                autoComplete={'new-password'}
                                id={'newPassword'}
                                isInvalid={!requirementsMet}
                                onBlur={checkRequirements}
                                onChange={handleChange}
                                type={getFieldType('newPassword')}
                                value={newPassword}
                            />
                            <Button aria-label={visibility.newPassword ? (formatMessage({ id: 'hide_password.b11980db', defaultMessage: 'Hide password' })) : (formatMessage({ id: 'show_password.774881b8', defaultMessage: 'Show password' }))} aria-pressed={visibility.newPassword} onClick={() => toggleHide('newPassword')} size={'sm'} variant={visibility.newPassword ? 'primary' : 'secondary'}>
                                <i aria-hidden="true" className={`fa-solid ${!visibility.newPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                            </Button>
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage defaultMessage="Minimum 8 characters, at least one uppercase letter, one lowercase letter, one number, and one special character." id="minimum_8_characters_at_least_one_upperc.02d5146b" />
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor={'confirmPassword'}>
                            <FormattedMessage defaultMessage="Confirm new password:" id="confirm_new_password.a44efa07" />
                        </Form.Label>
                        <InputGroup hasValidation>
                            <Form.Control
                                required
                                autoComplete={'new-password'}
                                id={'confirmPassword'}
                                isInvalid={!passwordsMatch}
                                onBlur={comparePasswords}
                                onChange={handleChange}
                                type={getFieldType('confirmPassword')}
                                value={confirmPassword}
                            />
                            <Button aria-label={visibility.confirmPassword ? (formatMessage({ id: 'hide_password.b11980db', defaultMessage: 'Hide password' })) : (formatMessage({ id: 'show_password.774881b8', defaultMessage: 'Show password' }))} aria-pressed={visibility.confirmPassword} onClick={() => toggleHide('confirmPassword')} size={'sm'} variant={visibility.confirmPassword ? 'primary' : 'secondary'}>
                                <i aria-hidden="true" className={`fa-solid ${!visibility.confirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                            </Button>
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage defaultMessage="Confirm Password - Password fields must match." id="confirm_password_password_fields_must_ma.b3b7ad9a" />
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>

                    <Form.Group>
                        <Button disabled={!canEdit || loading} type={'submit'}>
                            <FormattedMessage defaultMessage="Reset Password" id="reset_password.9c0200aa" />
                        </Button>
                    </Form.Group>
                </Col>
            </Row>

            <Dialog ref={confirmationDialogRef} />
        </div>
    );

};